import { BackupSyncState, LocalBackup, ValidationError } from '@app/domain/entities';
import type { IBackupRepository } from '@app/domain/ports';
import { BackupDataRecord, IndexedDbFactory } from '../factories/indexeddb-provider.factory';
import { BaseIndexDbRepository } from './base-indexdb.repository';
import { toArrayBuffer } from '../utils/array-buffer.utils';

export class BackupRepository extends BaseIndexDbRepository<LocalBackup> implements IBackupRepository {
  constructor(indexedDbFactory: IndexedDbFactory) {
    super(indexedDbFactory, 'backups');
  }

  async findAllByCreatedAt(descending = true): Promise<LocalBackup[]> {
    return this.executeStoreOperation('findAllByCreatedAt', { descending }, async (db) => {
      const backups = await db.backups.orderBy('createdAt').toArray();
      if (descending) backups.reverse();
      return backups;
    });
  }

  async getLastBackupTime(): Promise<number> {
    return this.executeStoreOperation('getLastBackupTime', {}, async (db) => {
      const backup = await db.backups.orderBy('createdAt').reverse().first();
      return backup ? backup.createdAt : 0;
    });
  }

  async findPendingBackups(): Promise<LocalBackup[]> {
    const backups = await this.findAll();
    return backups.filter(
      (b) => b.syncState === BackupSyncState.SYNC_PENDING || b.syncState === BackupSyncState.SYNC_FAILED,
    );
  }

  async updateSyncState(id: string, syncState: BackupSyncState, driveFileId?: string, error?: string): Promise<void> {
    await this.update(id, { syncState, driveFileId, error });
  }

  async updatePin(id: string, pinned: boolean): Promise<void> {
    await this.update(id, { isPinned: pinned });
  }

  // Binary data operations
  async saveData(backupId: string, data: Uint8Array): Promise<void> {
    await this.executeTransaction(
      'saveData',
      { backupId },
      (db) => [db.backups, db.backup_data],
      async (db) => {
        const entity = await db.backups.get(backupId);
        if (!entity) throw new ValidationError('backupId', `Backup with id ${backupId} can not be found`);

        await db.backup_data.put({ backupId, data: toArrayBuffer(data) });
        await db.backups.put({ ...entity, hasLocalData: true });
      },
    );
  }

  async findDataById(backupId: string): Promise<Uint8Array | null> {
    return this.executeStoreOperation(
      'findDataById',
      { backupId },
      async (db) => {
        const result = await db.backup_data.get(backupId);
        return result?.data ? new Uint8Array(result.data) : null;
      },
      'backup_data',
    );
  }

  async deleteData(backupId: string): Promise<void> {
    await this.executeTransaction(
      'deleteData',
      { backupId },
      (db) => [db.backups, db.backup_data],
      async (db) => {
        const entity = await db.backups.get(backupId);
        if (!entity) throw new ValidationError('backupId', `Backup with id ${backupId} can not be found`);

        await db.backup_data.delete(backupId);
        await db.backups.put({ ...entity, syncState: BackupSyncState.REMOTE_ONLY, hasLocalData: false });
      },
    );
  }

  async hasData(backupId: string): Promise<boolean> {
    return this.executeStoreOperation(
      'hasData',
      { backupId },
      async (db) => (await db.backup_data.get(backupId)) !== undefined,
      'backup_data',
    );
  }
}

// Re-export BackupDataRecord for consumers that need the type.
export type { BackupDataRecord };
